// Visual vehicle: a clone of a vehicles.glb model with its four wheel nodes, driven from a CarState.

import { Scene } from '@babylonjs/core/scene';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { MaterialPluginBase } from '@babylonjs/core/Materials/materialPluginBase';
import type { UniformBuffer } from '@babylonjs/core/Materials/uniformBuffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Ray } from '@babylonjs/core/Culling/ray';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import { ASSET_BASE, VEHICLE_YAW_OFFSET } from './config';
import type { CarState, ImpactContact } from '../sim/car';

/** Replace only the atlas's yellow paint, preserving windows, trim and lights. */
class TaxiPaint extends MaterialPluginBase {
  private colour: Color3;
  constructor(material: PBRMaterial, colour: Color3) { super(material, 'TaxiPaint', 200, {}, true, true); this.colour = colour.toLinearSpace(); }
  getUniforms() { return { ubo: [{ name: 'cabPaintColour', size: 3, type: 'vec3' }], fragment: '#ifndef UNIFORMBUFFERS\nuniform vec3 cabPaintColour;\n#endif' }; }
  bindForSubMesh(buffer: UniformBuffer): void { buffer.updateColor3('cabPaintColour', this.colour); }
  getCustomCode(shaderType: string): Record<string, string> | null {
    if (shaderType !== 'fragment') return null;
    return { CUSTOM_FRAGMENT_BEFORE_LIGHTS: `
      #ifdef ALBEDO
      if (albedoTexture.r > 0.55 && albedoTexture.g > albedoTexture.r * 0.45 && albedoTexture.b < albedoTexture.r * 0.35) {
        surfaceAlbedo = cabPaintColour * max(surfaceAlbedo.r, surfaceAlbedo.g);
      }
      #endif
    ` };
  }
}

export class VehicleLibrary {
  private constructor(readonly scene: Scene, readonly roots: Map<string, TransformNode>) {}

  static async load(scene: Scene): Promise<VehicleLibrary> {
    const c = await LoadAssetContainerAsync(`${ASSET_BASE}models/vehicles.glb`, scene);
    c.addAllToScene();
    const roots = new Map<string, TransformNode>();
    // bake the glTF root transform into each vehicle root so clones live in plain Babylon space
    const gltfRoot = c.meshes.find((m) => m.name === '__root__');
    const candidates: TransformNode[] = [...c.transformNodes, ...c.meshes].filter((n) => n.parent === gltfRoot && n !== gltfRoot);
    for (const m of candidates) {
      m.setParent(null);
      const holder = new TransformNode(`veh_${m.name}`, scene);
      // move the loader's mirror scaling into a holder so children keep their local transforms
      holder.scaling.copyFrom(m.scaling); holder.rotationQuaternion = m.rotationQuaternion?.clone() ?? null; holder.rotation.copyFrom(m.rotation);
      m.scaling.setAll(1); m.rotationQuaternion = null; m.rotation.setAll(0); m.position.setAll(0);
      m.parent = holder;
      holder.setEnabled(false);
      roots.set(m.name, holder);
    }
    for (const mat of c.materials) if (mat instanceof PBRMaterial) { mat.metallic = 0; mat.roughness = 0.9; }
    return new VehicleLibrary(scene, roots);
  }

  names(): string[] { return [...this.roots.keys()]; }

  spawn(model: string, tint: Color3 | null, shadows?: CascadedShadowGenerator): VehicleView {
    const src = this.roots.get(model) ?? this.roots.values().next().value!;
    const root = new TransformNode(`car_${model}`, this.scene);
    const clone = src.clone(`${model}_clone`, root, false)!;
    clone.setEnabled(true);
    const meshes = clone.getChildMeshes(false) as AbstractMesh[];
    const wheels: Record<string, TransformNode> = {};
    let body: AbstractMesh | null = null;
    for (const m of meshes) {
      const suffix = m.name.match(/_(FL|FR|RL|RR)(_primitive\d+)?$/)?.[1];
      if (suffix) wheels[suffix] = m; else if (!body) body = m;
      m.isPickable = false;
      shadows?.addShadowCaster(m, false);
      m.receiveShadows = true;
    }
    let paint: PBRMaterial | null = null;
    if (tint && body) {
      const mat = (body.material as PBRMaterial | null)?.clone(`tint_${model}_${Math.random().toString(36).slice(2, 7)}`) ?? null;
      if (mat) { new TaxiPaint(mat, tint); body.material = mat; paint = mat; }
    }
    const view = new VehicleView(root, body, wheels, paint);
    if (model.includes('Taxi')) view.feedback(1, 0);
    return view;
  }
}

export class VehicleView {
  private steerNodes: TransformNode[] = [];
  private sparks: Mesh[] = [];
  private scars: Mesh[] = [];
  private scarMaterial: StandardMaterial | null = null;
  private damageMeshes: AbstractMesh[] = [];
  private ownedMaterials: StandardMaterial[] = [];
  private hitTime = 0;
  private previousHealth = 1;
  private feedbackReady = false;
  constructor(readonly root: TransformNode, readonly body: AbstractMesh | null, readonly wheels: Record<string, TransformNode>, private paint: PBRMaterial | null = null) {
    // front wheels steer around a pivot node so spin and steer compose cleanly
    for (const k of ['FL', 'FR']) {
      const w = wheels[k]; if (!w) continue;
      const pivot = new TransformNode(`${k}_pivot`, root.getScene());
      pivot.parent = w.parent; pivot.position.copyFrom(w.position);
      w.parent = pivot; w.position.setAll(0);
      this.steerNodes.push(pivot);
    }
  }

  apply(car: CarState): void {
    this.root.position.set(car.x, car.y, car.z);
    this.root.rotation.set(0, car.yaw + VEHICLE_YAW_OFFSET, 0);
    for (const p of this.steerNodes) p.rotation.y = car.wheelAngle;
    for (const k in this.wheels) this.wheels[k].rotation.x = car.wheelSpin;
  }

  /** Only player taxis allocate feedback; civilian traffic stays cheap. */
  private initFeedback(): void {
    if (this.feedbackReady) return; this.feedbackReady = true;
    const scene = this.root.getScene();
    const material = (name: string, colour: string) => {
      const mat = new StandardMaterial(name, scene); mat.diffuseColor = Color3.FromHexString(colour);
      mat.emissiveColor = mat.diffuseColor; mat.disableLighting = true; mat.specularColor = Color3.Black();
      this.ownedMaterials.push(mat); return mat;
    };
    const sparkMat = material('collisionSparkMat', '#FFB83E');
    for (let i = 0; i < 8; i++) {
      const m = MeshBuilder.CreateBox('collisionSpark', { width: .14, height: .14, depth: .6 }, scene);
      m.parent = this.root; m.material = sparkMat; m.isPickable = false; m.setEnabled(false); this.sparks.push(m);
    }
    // Project scuffs onto the actual car surface, so they follow curved panels rather
    // than floating on a guessed bounding box. These are simple code-drawn scratches.
    const scarMat = material('taxiScuffMat', '#FFFFFF');
    const tex = new DynamicTexture('taxiScuffs', { width: 128, height: 128 }, scene, false);
    const ctx = tex.getContext(); ctx.clearRect(0, 0, 128, 128);
    // Dark gouges with exposed silver edges stay readable on every cab colour.
    for (const [colour, width] of [['#17181B', 16], ['#CFD3D6', 7]] as const) {
      ctx.strokeStyle = colour; ctx.lineWidth = width;
      for (let i = 0; i < 3; i++) { ctx.beginPath(); ctx.moveTo(12 + i * 10, 23 + i * 32); ctx.lineTo(60, 32 + i * 24); ctx.lineTo(116 - i * 7, 14 + i * 30); ctx.stroke(); }
    }
    tex.hasAlpha = true; tex.update(); scarMat.diffuseTexture = tex; scarMat.emissiveTexture = tex; scarMat.diffuseColor = Color3.Black(); scarMat.useAlphaFromDiffuseTexture = true; scarMat.zOffset = -2; scarMat.backFaceCulling = false;
    this.scarMaterial = scarMat;
    this.damageMeshes = this.root.getChildMeshes().filter(m => !this.sparks.includes(m as Mesh) && !/_(FL|FR|RL|RR)(_|$)/.test(m.name));
  }

  hit(contact?: ImpactContact, amount = 10): void {
    this.initFeedback(); this.hitTime = .32;
    if (!contact) return;
    // The simulation reports a point and outward normal in the car's local frame.
    // Cast back into the body from that side to place the decal on the real panel.
    const world = this.root.computeWorldMatrix(true);
    for (const mesh of this.damageMeshes) mesh.computeWorldMatrix(true);
    const normal = Vector3.TransformNormal(new Vector3(contact.nx, 0, contact.nz), world).normalize();
    const point = Vector3.TransformCoordinates(new Vector3(contact.x, .7, contact.z), world);
    const ray = new Ray(point.add(normal.scale(4)), normal.negate(), 8);
    const hit = this.root.getScene().pickWithRay(ray, m => this.damageMeshes.includes(m));
    if (!hit?.pickedPoint || !(hit.pickedMesh instanceof Mesh)) return;
    const size = .4 + Math.min(1, amount / 35) * .65;
    const decal = MeshBuilder.CreateDecal('taxiDamageDecal', hit.pickedMesh, {
      position: hit.pickedPoint, normal: hit.getNormal(true) ?? normal,
      size: new Vector3(size, size * .7, .25),
    });
    if (!decal.getTotalVertices()) { decal.dispose(); return; }
    decal.setParent(this.root); decal.material = this.scarMaterial; decal.isPickable = false;
    decal.metadata = { contact: { ...contact }, amount };
    this.scars.push(decal);
    if (this.scars.length > 12) this.scars.shift()!.dispose();
  }

  feedback(health: number, dt: number): void {
    // Only real contacts add scars; a health change alone must not invent an impact site.
    this.initFeedback();
    if (health < this.previousHealth - .005) this.hit();
    if (health >= .999 && this.previousHealth < .999) { for (const scar of this.scars) scar.dispose(); this.scars = []; }
    this.previousHealth = health;
    this.hitTime = Math.max(0, this.hitTime - dt);
    const progress = 1 - this.hitTime / .32;
    for (let i = 0; i < this.sparks.length; i++) {
      const m = this.sparks[i], angle = i * Math.PI / 4;
      m.setEnabled(this.hitTime > 0);
      m.position.set(Math.sin(angle) * (1.3 + progress * 2), .7 + Math.sin(progress * Math.PI), Math.cos(angle) * (2 + progress * 2));
      m.rotation.y = angle; m.scaling.setAll(1 - progress * .8);
    }
  }

  dispose(): void { this.root.dispose(false, false); this.paint?.dispose(); for (const m of this.ownedMaterials) m.dispose(false, true); }
}
